import { System } from '../System';
import { Entity } from '../Entity';
import { TransformComponent } from '../components/TransformComponent';
import { HealthComponent } from '../components/HealthComponent';
import { SpriteComponent } from '../components/SpriteComponent';
import { InventoryComponent } from '../components/InventoryComponent';
import { ItemRegistry } from '../../data/ItemRegistry';
import { EventBus } from '../../utils/EventBus';

/**
 * Shrine zone definition
 */
interface ShrineZone {
  id: string;
  bounds: { x: number; y: number; width: number; height: number };
  name: string;
}

/**
 * RevivalSystem - Handles revival of fainted party members
 * - Item-based revival through inventory
 * - Shrine-based revival at special locations
 */
export class RevivalSystem extends System {
  public readonly requiredComponents = ['inventory'];

  /** Registered shrine zones */
  private shrines: Map<string, ShrineZone> = new Map();

  /** Currently tracked shrine proximity for player */
  private playerNearShrine: string | null = null;

  /** Revival health percent (25%) */
  private revivalHealthPercent = 0.25;

  /** Shrine healing rate (HP per second) */
  private shrineHealRate = 5;

  /** Time since last shrine heal tick */
  private shrineHealTimer = 0;

  /** Shrine heal tick interval (ms) */
  private shrineHealInterval = 500;

  public init(): void {
    // Listen for item use requests
    EventBus.onEvent('inventory:use_item', (data) => {
      this.handleItemUse(data.itemId, data.targetEntityId);
    });

    // Listen for trigger zone registration (shrines)
    EventBus.onEvent('trigger:register', (data) => {
      if (data.type === 'revival_shrine') {
        this.registerShrine(data);
      }
    });
  }

  /**
   * Register shrines from trigger zones array (called after tilemap loads)
   */
  public registerShrinesFromZones(
    zones: Array<{
      name: string;
      type: string;
      x: number;
      y: number;
      width: number;
      height: number;
      properties: Record<string, unknown>;
    }>
  ): void {
    for (const zone of zones) {
      if (zone.type === 'revival_shrine') {
        this.registerShrine({
          name: zone.name,
          type: zone.type,
          bounds: {
            x: zone.x,
            y: zone.y,
            width: zone.width,
            height: zone.height,
          },
          properties: zone.properties,
        });
      }
    }
  }

  public update(entities: Entity[], delta: number): void {
    // Check player proximity to shrines
    for (const entity of entities) {
      if (!entity.hasTag('player')) continue;

      const transform = entity.getComponent<TransformComponent>('transform');
      if (!transform) continue;

      this.checkShrineProximity(transform.x, transform.y);
    }

    // Passive shrine healing when player is near a shrine
    if (this.playerNearShrine) {
      this.shrineHealTimer += delta;

      if (this.shrineHealTimer >= this.shrineHealInterval) {
        this.shrineHealTimer = 0;
        this.applyShrineHealing();
      }
    } else {
      this.shrineHealTimer = 0;
    }
  }

  /**
   * Apply passive healing to all party members while on shrine
   */
  private applyShrineHealing(): void {
    const partyMembers = this.world.getEntitiesByTag('party_member');
    const healAmount = Math.floor((this.shrineHealRate * this.shrineHealInterval) / 1000);

    for (const member of partyMembers) {
      const health = member.getComponent<HealthComponent>('health');
      if (!health) continue;

      // Revive fainted members first
      if (health.isFainted) {
        health.revive(this.revivalHealthPercent);

        // Restore sprite
        const sprite = member.getComponent<SpriteComponent>('sprite');
        if (sprite) {
          sprite.setTint(0xffffff);
          sprite.alpha = 1;
          sprite.sprite?.setAlpha(1);
        }

        EventBus.emitEvent('beastie:revived', {
          entityId: member.id,
          revivedBy: 'shrine',
          healthPercent: this.revivalHealthPercent,
        });
      } else if (health.isAlive && health.current < health.max) {
        // Heal living members that aren't at full health
        const actualHeal = health.heal(healAmount);
        if (actualHeal > 0) {
          EventBus.emitEvent('beastie:healed', {
            id: member.id,
            amount: actualHeal,
          });
        }
      }
    }

    // Also heal the player
    const player = this.world.getEntitiesByTag('player')[0];
    if (player) {
      const playerHealth = player.getComponent<HealthComponent>('health');
      if (playerHealth && playerHealth.isAlive && playerHealth.current < playerHealth.max) {
        const actualHeal = playerHealth.heal(healAmount);
        if (actualHeal > 0) {
          EventBus.emitEvent('player:healed', {
            amount: actualHeal,
          });
        }
      }
    }
  }

  /**
   * Register a shrine zone
   */
  private registerShrine(data: {
    name: string;
    type: string;
    bounds: unknown;
    properties: unknown;
  }): void {
    const bounds = data.bounds as { x: number; y: number; width: number; height: number };
    const shrine: ShrineZone = {
      id: data.name,
      bounds,
      name: data.name,
    };
    this.shrines.set(shrine.id, shrine);
    console.log(`RevivalSystem: Registered shrine '${shrine.id}'`);
  }

  /**
   * Check if player is near any shrine
   */
  private checkShrineProximity(playerX: number, playerY: number): void {
    let nearestShrine: string | null = null;

    for (const [id, shrine] of this.shrines) {
      if (this.isInBounds(playerX, playerY, shrine.bounds)) {
        nearestShrine = id;
        break;
      }
    }

    // Emit event if proximity changed
    if (nearestShrine !== this.playerNearShrine) {
      if (nearestShrine) {
        EventBus.emitEvent('shrine:nearby', { shrineId: nearestShrine, inRange: true });
      } else if (this.playerNearShrine) {
        EventBus.emitEvent('shrine:nearby', { shrineId: this.playerNearShrine, inRange: false });
      }
      this.playerNearShrine = nearestShrine;
    }
  }

  /**
   * Check if point is within bounds
   */
  private isInBounds(
    x: number,
    y: number,
    bounds: { x: number; y: number; width: number; height: number }
  ): boolean {
    return (
      x >= bounds.x &&
      x <= bounds.x + bounds.width &&
      y >= bounds.y &&
      y <= bounds.y + bounds.height
    );
  }

  /**
   * Handle item use for revival
   */
  private handleItemUse(itemId: string, targetEntityId?: string): void {
    const item = ItemRegistry.get(itemId);
    if (!item?.effect) {
      console.warn(`RevivalSystem: Item '${itemId}' has no effect`);
      return;
    }

    // Get player entity (inventory holder)
    const player = this.world.getEntitiesByTag('player')[0];
    if (!player) return;

    const inventory = player.getComponent<InventoryComponent>('inventory');
    if (!inventory || !inventory.hasItem(itemId)) {
      console.warn(`RevivalSystem: Player doesn't have item '${itemId}'`);
      return;
    }

    // Handle based on effect type
    if (item.effect.type === 'revive') {
      if (!targetEntityId) {
        console.warn('RevivalSystem: Revive item requires a target');
        return;
      }
      this.reviveWithItem(itemId, targetEntityId, inventory);
    } else if (item.effect.type === 'heal') {
      if (!targetEntityId) {
        console.warn('RevivalSystem: Heal item requires a target');
        return;
      }
      this.healWithItem(itemId, targetEntityId, inventory);
    }
  }

  /**
   * Revive a fainted beastie with an item
   */
  private reviveWithItem(
    itemId: string,
    targetEntityId: string,
    inventory: InventoryComponent
  ): void {
    const target = this.world.getEntity(targetEntityId);
    if (!target) {
      console.warn(`RevivalSystem: Target entity '${targetEntityId}' not found`);
      return;
    }

    const health = target.getComponent<HealthComponent>('health');
    if (!health || !health.isFainted) {
      console.warn('RevivalSystem: Target is not fainted');
      return;
    }

    const item = ItemRegistry.get(itemId)!;
    const revivePercent = item.effect?.amount ?? this.revivalHealthPercent;

    // Revive the target
    health.revive(revivePercent);

    // Restore sprite appearance
    const sprite = target.getComponent<SpriteComponent>('sprite');
    if (sprite) {
      sprite.setTint(0xffffff); // Remove gray tint
      sprite.alpha = 1;
      sprite.sprite?.setAlpha(1); // Also set actual Phaser sprite alpha
    }

    // Remove item from inventory
    inventory.removeItem(itemId);

    // Emit events
    EventBus.emitEvent('beastie:revived', {
      entityId: targetEntityId,
      revivedBy: 'item',
      healthPercent: revivePercent,
    });

    EventBus.emitEvent('inventory:item_used', {
      itemId,
      targetEntityId,
      effect: 'revive',
    });

    console.log(`RevivalSystem: Revived ${targetEntityId} with ${itemId}`);
  }

  /**
   * Heal a damaged beastie with an item
   */
  private healWithItem(
    itemId: string,
    targetEntityId: string,
    inventory: InventoryComponent
  ): void {
    const target = this.world.getEntity(targetEntityId);
    if (!target) return;

    const health = target.getComponent<HealthComponent>('health');
    if (!health || health.isFainted || !health.isAlive) {
      console.warn('RevivalSystem: Cannot heal dead or fainted target');
      return;
    }

    const item = ItemRegistry.get(itemId)!;
    const healPercent = item.effect?.amount ?? 0.2;
    const healAmount = Math.floor(health.max * healPercent);

    health.heal(healAmount);
    inventory.removeItem(itemId);

    EventBus.emitEvent('beastie:healed', {
      id: targetEntityId,
      amount: healAmount,
    });

    EventBus.emitEvent('inventory:item_used', {
      itemId,
      targetEntityId,
      effect: 'heal',
    });

    console.log(`RevivalSystem: Healed ${targetEntityId} for ${healAmount} HP`);
  }

  /**
   * Use shrine to revive all fainted party members
   */
  public useShrine(shrineId: string): number {
    if (!this.shrines.has(shrineId)) {
      console.warn(`RevivalSystem: Unknown shrine '${shrineId}'`);
      return 0;
    }

    const partyMembers = this.world.getEntitiesByTag('party_member');
    let revivedCount = 0;

    for (const member of partyMembers) {
      const health = member.getComponent<HealthComponent>('health');
      if (health?.isFainted) {
        health.revive(this.revivalHealthPercent);

        // Restore sprite
        const sprite = member.getComponent<SpriteComponent>('sprite');
        if (sprite) {
          sprite.setTint(0xffffff);
          sprite.alpha = 1;
          sprite.sprite?.setAlpha(1);
        }

        EventBus.emitEvent('beastie:revived', {
          entityId: member.id,
          revivedBy: 'shrine',
          healthPercent: this.revivalHealthPercent,
        });

        revivedCount++;
      }
    }

    if (revivedCount > 0) {
      EventBus.emitEvent('shrine:used', { shrineId, revivedCount });
      console.log(`RevivalSystem: Shrine revived ${revivedCount} beasties`);
    }

    return revivedCount;
  }

  /**
   * Get the shrine the player is currently near
   */
  public getNearbyShrine(): string | null {
    return this.playerNearShrine;
  }

  /**
   * Debug shrine proximity - logs player position and all shrine bounds
   */
  public debugShrineProximity(): void {
    const player = this.world.getEntitiesByTag('player')[0];
    if (!player) {
      console.log('Debug: No player found');
      return;
    }

    const transform = player.getComponent<TransformComponent>('transform');
    if (!transform) {
      console.log('Debug: Player has no transform');
      return;
    }

    console.log(`Debug: Player position (${transform.x.toFixed(2)}, ${transform.y.toFixed(2)})`);
    console.log(`Debug: Registered shrines: ${this.shrines.size}`);

    for (const [id, shrine] of this.shrines) {
      const inBounds = this.isInBounds(transform.x, transform.y, shrine.bounds);
      console.log(`Debug: Shrine '${id}' bounds: x=${shrine.bounds.x.toFixed(2)}-${(shrine.bounds.x + shrine.bounds.width).toFixed(2)}, y=${shrine.bounds.y.toFixed(2)}-${(shrine.bounds.y + shrine.bounds.height).toFixed(2)}, playerInBounds=${inBounds}`);
    }
  }

  /**
   * Check if any party members are fainted
   */
  public hasFaintedPartyMembers(): boolean {
    const partyMembers = this.world.getEntitiesByTag('party_member');
    return partyMembers.some((member) => {
      const health = member.getComponent<HealthComponent>('health');
      return health?.isFainted;
    });
  }

  public destroy(): void {
    this.shrines.clear();
    this.playerNearShrine = null;
  }
}
